<template>
  <div class="TagInput">
    <div
      class="TagInput__inputWrapper"
      :class="{ 'TagInput__inputWrapper--error': isError }"
    >
      <div class="TagInput__tags" @click="focusInput">
        <span
          v-if="!hasContent"
          class="TagInput__placeholder"
          :title="placeholder"
        >
          {{ placeholder }}
        </span>
        <span
          v-for="(tag, index) in internalTags"
          :key="index"
          class="TagInput__tag"
        >
          {{ tag }}
          <span
            v-if="removable"
            class="TagInput__remove"
            @click.stop="removeTag(index)"
            >&times;</span
          >
        </span>

        <input
          class="TagInput__input"
          :type="type"
          v-model="newTag"
          :disabled="disabled || isMaxReached"
          :readonly="readonly"
          :style="{ backgroundColor: isMaxReached ? 'transparent' : '' }"
          @keydown="handleKeydown"
          @input="handleInputChange"
          @paste="handlePaste"
          @blur="addTag"
          ref="inputRef"
        />
      </div>
    </div>
    <p v-if="isError && errorText" class="TagInput__error">
      {{ errorText }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, nextTick, watch, computed } from 'vue';
import { mailValidate } from '@spectee/ts/validation';

// ========================
// Props & Emits
// ========================

interface Props {
  modelValues: string[];
  placeholder?: string;
  type?: string;
  allowDuplicates?: boolean;
  maxTags?: number;
  disabled?: boolean;
  readonly?: boolean;
  removable?: boolean;
  separators?: string[];
  isError: boolean;
  errorText: string;
  isEmail: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  modelValues: () => [],
  type: 'text',
  removable: true,
  separators: () => ['Enter'],
  isEmail: true,
});

const emit = defineEmits(['update:modelValues', 'update:isInvalidMailPresent']);

// ========================
// Refs & State
// ========================
const internalTags = ref<string[]>([...props.modelValues]);
const newTag = ref('');
const inputRef = ref<HTMLInputElement | null>(null);
const isInvalidValueDeleted = ref(false);

// ========================
// Watch for External Model Updates
// ========================
watch(
  () => props.modelValues,
  newVal => {
    internalTags.value = [...newVal];
  }
);

const separatorChars = computed(() => {
  return props.separators.map(sep => getSeparatorChars(sep));
});

const getSeparatorChars = (sep: string) => {
  switch (sep) {
    case 'Enter':
      return '\n';
    case 'Tab':
      return '\t';
    case 'Comma':
      return ',';
    case 'Space':
      return ' ';
    default:
      return sep; // if they directly use characters like ',' or ' '
  }
};

const isMaxReached = computed(() => {
  return props.maxTags != null && internalTags.value.length >= props.maxTags;
});

const hasContent = computed(() => {
  return newTag.value.length > 0 || internalTags.value.length > 0;
});

const handleInputChange = (event: Event) => {
  const inputValue = (event.target as HTMLInputElement).value;
  if (isInvalidValueDeleted.value) {
    emit('update:isInvalidMailPresent', inputValue.length > 0);
    isInvalidValueDeleted.value = false;
  }
};
const handleKeydown = async (event: KeyboardEvent) => {
  const key = event.key;
  await nextTick();
  if (props.separators.includes(key)) {
    event.preventDefault(); // Prevent default behavior for keys like Enter or comma
    addTag(event);
  } else if (key === 'Backspace' || key === 'Delete') {
    isInvalidValueDeleted.value = false;
    removeLastTag();
  }
};

const handlePaste = (event: ClipboardEvent) => {
  event.preventDefault(); // Stop the default paste behavior
  const pastedData = event.clipboardData?.getData('text');
  if (pastedData) {
    newTag.value += pastedData;
    addTag();
  }
};
// ========================
// Tag Logic
// ========================
const addTag = (event?: Event) => {
  const tagInput = newTag.value.trim();

  if (!tagInput) return;

  // Split input based on separators (e.g., comma, semicolon)
  const separatorRegex = new RegExp(`[${separatorChars.value.join('')}]`);
  const potentialTags = tagInput
    .split(separatorRegex)
    .map(tag => tag.trim())
    .filter(tag => tag.length > 0);

  const validTags: string[] = [];
  const invalidTags: string[] = [];

  for (const tag of potentialTags) {
    // Stop if maxTags limit is reached
    if (
      props.maxTags !== undefined &&
      internalTags.value.length >= props.maxTags
    ) {
      break;
    }

    const isDuplicate = internalTags.value.includes(tag);
    const isValid = props.isEmail ? mailValidate(tag) : true;

    if (isValid && (props.allowDuplicates || !isDuplicate)) {
      validTags.push(tag);
    } else if (!isValid) {
      invalidTags.push(tag);
    }
  }

  if (validTags.length) {
    internalTags.value.push(...validTags);
    emit('update:modelValues', internalTags.value);
  }

  // Show all invalids joined by separator
  if (invalidTags.length > 0) {
    newTag.value = invalidTags.join(', ');

    // If called from keydown, we may have a key
    if (event instanceof KeyboardEvent && event.key) {
      newTag.value += getSeparatorChars(event.key);
    }
  } else {
    newTag.value = '';
  }
  emit('update:isInvalidMailPresent', newTag.value?.length > 0);
  nextTick(() => inputRef.value?.focus());
};

const removeTag = (index: number) => {
  internalTags.value.splice(index, 1);
  emit('update:modelValues', internalTags.value);
};

const removeLastTag = async () => {
  if (newTag.value === '') {
    internalTags.value.pop();

    emit('update:modelValues', internalTags.value);
  } else {
    isInvalidValueDeleted.value = true;
  }
};

const focusInput = () => {
  inputRef.value?.focus();
};

onMounted(() => {
  nextTick(() => inputRef.value?.focus());
});
</script>

<style lang="scss" scoped>
@use '@spectee/assets/scss/_pageForward.scss' as *;

.TagInput {
  position: relative;
  width: 100%;

  &__inputWrapper {
    position: relative;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: $service-unit * 2;
    border: 1px solid #ccc;
    padding: $service-unit * 2;
    border-radius: $service-border-radius * 2;
    overflow: hidden;

    &:focus-within {
      outline: 2px solid $color-blue-60;
    }
  }

  &__tags {
    width: 100%;
    display: flex;
    flex-wrap: wrap;
    gap: $service-unit * 2;
  }

  &__tag {
    background-color: #eaf0fb;
    padding: $service-unit $service-unit * 2;
    border-radius: $service-border-radius * 12;
    display: flex;
    align-items: center;
    font-size: $service-font-size-p;
  }

  &__remove {
    margin-left: $service-unit * 2;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: $service-unit * 4;
    height: $service-unit * 4;
    font-size: $service-font-size-ff; // Controls the size of the × symbol
    line-height: 1;
  }

  &__input {
    border: none;
    outline: none;
    appearance: none;
    font-size: $service-font-size-p;
    padding: $service-unit;
    width: 100%;
    flex-grow: 1;
    background-color: #fff;
    caret-color: $color-blue-60;
  }

  &__error {
    color: red;
    font-size: $service-font-size-pp;
    margin-top: $service-unit;
  }

  &__inputWrapper--error {
    border: 2px solid $color-red-70;

    &:hover {
      border: 2px solid $color-red-70;
    }
  }

  &__placeholder {
    color: $color-gray-50;
    font-size: $service-font-size-p;
    position: absolute;
    pointer-events: auto;
    padding-left: $service-unit;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
    display: inline-block;
  }
}
</style>
